import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Search, Eye, Trash2, BookOpen, User, Calendar, MoreVertical, Edit2, List } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getCourses, deleteCourse } from '@/lib/firebase/queries/courses';
import { getInstructors } from '@/lib/firebase/queries/users';
import type { Course } from '@/components/education/types';
import { useAuth } from '@/contexts/AuthContext';
import { updateCourse } from '@/lib/firebase/queries/courses';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AdminModuleManager } from './AdminModuleManager';
import { toast } from 'sonner';

interface CourseWithInstructor extends Course {
  instructorName: string;
  instructorNameEn: string;
  instructorNameAr: string;
  createdAt?: Date;
}

export function AdminCourseManagement() {
  const { t, dir } = useLanguage();
  const [courses, setCourses] = useState<CourseWithInstructor[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<CourseWithInstructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<CourseWithInstructor | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState<CourseWithInstructor | null>(null);
  const [unitsDialogOpen, setUnitsDialogOpen] = useState(false);
  const [courseToEditUnits, setCourseToEditUnits] = useState<CourseWithInstructor | null>(null);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      
      // Fetch courses using the existing query
      const allCourses = await getCourses();
      
      // Fetch instructors to get instructor names
      const instructors = await getInstructors();
      const instructorsMap = new Map();
      instructors.forEach(instructor => {
        instructorsMap.set(instructor.id, {
          name: instructor.name,
          nameEn: instructor.nameEn,
          nameAr: instructor.nameAr
        });
      });

      const coursesWithInstructors: CourseWithInstructor[] = allCourses.map(course => {
        const instructorData = instructorsMap.get(course.instructorId);
        return {
          ...course,
          instructorName: instructorData?.name || 'Unknown Instructor',
          instructorNameEn: instructorData?.nameEn || 'Unknown Instructor',
          instructorNameAr: instructorData?.nameAr || 'مدرب غير معروف',
          createdAt: new Date() // Since createdAt is not in the Course type, we'll use current date
        };
      });

      setCourses(coursesWithInstructors);
      setFilteredCourses(coursesWithInstructors);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(t('dashboard.admin.courses.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCourses(courses);
    } else {
      const filtered = courses.filter(course =>
        course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.instructorNameEn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.instructorNameAr?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCourses(filtered);
    }
  }, [searchQuery, courses]);

  const handleDeleteClick = (course: CourseWithInstructor) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (course: CourseWithInstructor) => {
    setCourseToEdit(course);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setCourseToEdit(null);
    // Refresh courses after editing
    fetchCourses();
  };

  const handleEditUnitsClick = (course: CourseWithInstructor) => {
    setCourseToEditUnits(course);
    setUnitsDialogOpen(true);
  };

  const handleUnitsClose = () => {
    setUnitsDialogOpen(false);
    setCourseToEditUnits(null);
    // Refresh courses after editing units
    fetchCourses();
  };

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return;

    try {
      setDeleting(true);
      await deleteCourse(courseToDelete.id);
      
      setCourses(prev => prev.filter(c => c.id !== courseToDelete.id));
      toast.success(t('dashboard.admin.courses.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(t('dashboard.admin.courses.messages.deleteError'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'nutrition':
        return 'bg-green-100 text-green-800';
      case 'fitness':
        return 'bg-blue-100 text-blue-800';
      case 'mentalhealth':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('dashboard.admin.courses.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.admin.courses.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('dashboard.admin.courses.description')}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('dashboard.admin.courses.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredCourses.length} {t('dashboard.admin.courses.of')} {courses.length} {t('dashboard.admin.courses.coursesCount')}
        </div>
      </div>

      {/* Courses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {t('dashboard.admin.courses.allCourses')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCourses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? t('dashboard.admin.courses.noCoursesFound') : t('dashboard.admin.courses.noCourses')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dashboard.admin.courses.tableHeaders.course')}</TableHead>
                    <TableHead>{t('dashboard.admin.courses.tableHeaders.instructor')}</TableHead>
                    <TableHead>{t('dashboard.admin.courses.tableHeaders.category')}</TableHead>
                    <TableHead>{t('dashboard.admin.courses.tableHeaders.lessons')}</TableHead>
                    <TableHead>{t('dashboard.admin.courses.tableHeaders.certificate')}</TableHead>
                    <TableHead>{t('dashboard.admin.courses.tableHeaders.created')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.admin.courses.tableHeaders.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCourses.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{course.title}</div>
                          <div className="text-sm text-muted-foreground line-clamp-2">
                            {course.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {dir === 'rtl' ? course.instructorNameAr : course.instructorNameEn}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getCategoryBadgeColor(course.category)}>
                          {course.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4 text-muted-foreground" />
                          {course.totalLessons || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={course.certificate ? "default" : "secondary"}>
                          {course.certificate ? t('dashboard.admin.courses.certificateStatus.yes') : t('dashboard.admin.courses.certificateStatus.no')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {course.createdAt?.toLocaleDateString() || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditClick(course)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              {t('dashboard.admin.courses.actions.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditUnitsClick(course)}>
                              <List className="h-4 w-4 mr-2" />
                              {t('dashboard.admin.courses.actions.editUnits')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteClick(course)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t('dashboard.admin.courses.actions.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Course Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={handleEditClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.courses.editCourse')}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[70vh] p-4">
            {courseToEdit && <AdminCourseEditForm course={courseToEdit} onSave={handleEditClose} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Units Dialog */}
      <Dialog open={unitsDialogOpen} onOpenChange={handleUnitsClose}>
        <DialogContent className="w-[90vw] max-w-none h-[95vh] max-h-none overflow-hidden">
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.courses.editUnits')}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[85vh] p-4">
            {courseToEditUnits && <AdminCourseEditor course={courseToEditUnits} onSave={handleUnitsClose} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboard.admin.courses.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboard.admin.courses.deleteDialog.description', { title: courseToDelete?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboard.admin.courses.deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? t('dashboard.admin.courses.deleteDialog.deleting') : t('dashboard.admin.courses.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Admin Course Edit Form Component
interface AdminCourseEditFormProps {
  course: CourseWithInstructor;
  onSave: () => void;
}

function AdminCourseEditForm({ course, onSave }: AdminCourseEditFormProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: course.title,
    description: course.description,
    category: course.category,
    certificate: course.certificate,
    totalLessons: course.totalLessons
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateCourse(course.id, formData);
      toast.success(t('dashboard.admin.courses.messages.updateSuccess'));
      onSave();
    } catch (error) {
      console.error('Error updating course:', error);
      toast.error(t('dashboard.admin.courses.messages.updateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">{t('dashboard.admin.courses.form.title')}</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder={t('dashboard.admin.courses.form.titlePlaceholder')}
        />
      </div>

      <div>
        <Label htmlFor="description">{t('dashboard.admin.courses.form.description')}</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder={t('dashboard.admin.courses.form.descriptionPlaceholder')}
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="category">{t('dashboard.admin.courses.form.category')}</Label>
        <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
          <SelectTrigger>
            <SelectValue placeholder={t('dashboard.admin.courses.form.categoryPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nutrition">{t('dashboard.admin.courses.categories.nutrition')}</SelectItem>
            <SelectItem value="fitness">{t('dashboard.admin.courses.categories.fitness')}</SelectItem>
            <SelectItem value="mentalhealth">{t('dashboard.admin.courses.categories.mentalHealth')}</SelectItem>
            <SelectItem value="general">{t('dashboard.admin.courses.categories.general')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="totalLessons">{t('dashboard.admin.courses.form.totalLessons')}</Label>
        <Input
          id="totalLessons"
          type="number"
          value={formData.totalLessons}
          onChange={(e) => setFormData(prev => ({ ...prev, totalLessons: Number(e.target.value) }))}
          placeholder="0"
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="certificate"
          checked={formData.certificate}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, certificate: !!checked }))}
        />
        <Label htmlFor="certificate">{t('dashboard.admin.courses.form.certificate')}</Label>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onSave}>
          {t('dashboard.admin.courses.form.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? t('dashboard.admin.courses.form.saving') : t('dashboard.admin.courses.form.save')}
        </Button>
      </div>
    </div>
  );
}

// Admin Course Editor Component - Replicates instructor's module management interface
interface AdminCourseEditorProps {
  course: CourseWithInstructor;
  onSave: () => void;
}

function AdminCourseEditor({ course, onSave }: AdminCourseEditorProps) {
  return (
    <div className="h-full">
      <AdminModuleManager course={course} onClose={onSave} />
    </div>
  );
}
